import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

import { BucketType, TimeRange } from '@/schema'

import { DetailDrawerType, ViewLevel } from './constants'
import { IDashboardFilters } from './types'
import { coerceTimeRangeForPeriod } from './utils'

interface IDashboardState {
  filters: IDashboardFilters
  displayedDrawer: DetailDrawerType | null
  aggregationPeriod: BucketType
  timeRange: TimeRange
  viewLevel: ViewLevel
}

interface IDashboardStore extends IDashboardState {
  // actions
  setFilter: (filter: Partial<IDashboardFilters>) => void
  setDisplayedDrawer: (drawer: DetailDrawerType | null) => void
  setAggregationPeriod: (period: BucketType) => void
  setTimeRange: (timeRange: TimeRange) => void
  setViewLevel: (level: ViewLevel) => void
  reset: () => void
}

const initialState: IDashboardState = {
  filters: {
    companyId: null,
    projectId: null,
    deliverableSetId: null,
  },
  viewLevel: ViewLevel.ORG,
  displayedDrawer: null,
  aggregationPeriod: BucketType.week,
  timeRange: TimeRange.this_month,
}

export const useDashboardStore = create<IDashboardStore>()(
  devtools(
    (set) => ({
      ...initialState,
      /**
       * Set a filter value
       */
      setFilter: (payload: Partial<IDashboardFilters>) => {
        set(
          (state) => ({ filters: { ...state.filters, ...payload } }),
          false,
          'setFilter'
        )
      },

      /**
       * Set the displayed drawer
       */
      setDisplayedDrawer: (drawer: DetailDrawerType | null) => {
        set({ displayedDrawer: drawer }, false, 'setDisplayedDrawer')
      },

      /**
       * Set the aggregation period
       */
      setAggregationPeriod: (period: BucketType) => {
        set(
          (state) => ({
            aggregationPeriod: period,
            timeRange: coerceTimeRangeForPeriod(period, state.timeRange),
          }),
          false,
          'setAggregationPeriod'
        )
      },

      /**
       * Set the time range
       */
      setTimeRange: (timeRange) =>
        set(
          (state) => ({
            timeRange: coerceTimeRangeForPeriod(
              state.aggregationPeriod,
              timeRange
            ),
          }),
          false,
          'setTimeRange'
        ),
      setViewLevel: (level: ViewLevel) => {
        set({ viewLevel: level }, false, 'setViewLevel')
      },

      /**
       * Reset all filters to initial state
       */
      reset: () => {
        set(
          {
            filters: initialState.filters,
            displayedDrawer: null,
            aggregationPeriod: initialState.aggregationPeriod,
            timeRange: initialState.timeRange,
          },
          false,
          'reset'
        )
      },
    }),
    {
      name: 'DashboardStore',
    }
  )
)
